import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { type OverviewResponse } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

const { coachingAnalytics } = appStrings.modules;

export const useAgentContext = (overview: OverviewResponse) => {
  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.metrics.students,
    display: `Overview cards for total students, students active in the last 30 days${
      overview.coaches ? ', and coach headcount' : ''
    }, with the student count broken down by enrollment and by program.`,
    detail: [
      coachingAnalytics.metrics.studentsInfo,
      coachingAnalytics.metrics.engagementInfo,
      ...(overview.coaches ? [coachingAnalytics.metrics.coachesInfo] : []),
    ].join('\n\n'),
    data: {
      students: overview.students.total,
      by_stage: overview.students.by_stage,
      by_program: overview.students.by_program,
      recently_active: overview.recently_active,
      ...(overview.coaches ? { coaches: overview.coaches } : {}),
    },
  });

  useCoverageAgentContext(overview);
};

const useCoverageAgentContext = (overview: OverviewResponse, enabled = true) => {
  useRegisterAgentContext(
    enabled
      ? {
          kind: 'visual',
          label: coachingAnalytics.assessmentCoverage.title,
          display:
            'Horizontal bars showing the share of students who have completed each assessment: Blueprint, Are You On Track?, and Life Readiness.',
          detail: coachingAnalytics.assessmentCoverage.info,
          data: overview.completions,
        }
      : null,
  );
};

/** The coach's own Coaching section: My students, plus Assessment Coverage and My activity in detail. */
export const useMyCoachingAgentContext = (overview: OverviewResponse, detail: boolean) => {
  useRegisterAgentContext({
    kind: 'section',
    title: coachingAnalytics.myCoaching.title,
    description: coachingAnalytics.myCoaching.subtitle,
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.myStudents.title,
    display:
      "Vertical bars of the viewing coach's assigned students, switchable between by grade and by enrollment stage.",
    detail: coachingAnalytics.myStudents.info,
    data: {
      students: overview.students.total,
      by_grade: overview.students.by_grade,
      by_stage: overview.students.by_stage,
    },
  });

  useCoverageAgentContext(overview, detail);

  useRegisterAgentContext(
    detail && overview.my_activity
      ? {
          kind: 'visual',
          label: coachingAnalytics.myActivity.title,
          display: "Line chart of the viewing coach's own feed posts and comments per month.",
          detail: coachingAnalytics.myActivity.info,
          data: {
            buckets: overview.my_activity.monthly.buckets,
            series: overview.my_activity.monthly.series.map(
              ({ index, start, posts, comments }) => ({
                index,
                start,
                posts,
                comments,
              }),
            ),
          },
        }
      : null,
  );
};
